import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import {
  ItemLocationForm,
  ItemLocationFilter,
} from '../../../../../constants/constant_form_names';
import {
  BATCH,
  BUNDLE,
  INVENTORY,
  MATRIX,
  SERIALIZED,
  NONINVENTORY,
  SHIPPING,
  SERVICE,
  DISCOUNT,
  GIFTCERTIFICATE,
  LOYALTY,
  COUPON,
  PROMOTION,
  PAYMENT_SURCHARGE,
  SERVICE_CHARGE,
  GRATUITY,
} from '../../../../../constants/constant_item_types';

const selector = formValueSelector(ItemLocationForm);

const getFilter = state => {
  const selector = formValueSelector(ItemLocationFilter);

  return selector(
    state,
    'currency',
    'location',
    'price_book_reference',
    'item',
  );
};

const getFormCustomFields = state => selector(state, 'custom_fields') || {};

const getFormCustomTabs = state => selector(state, 'custom_tabs') || [];

const getFormItemTypeId = state => selector(state, 'item_type');

const getFormItemLocationId = state => selector(state, 'id');

const getFormLocationCurrency = state => selector(state, 'currency');

const getFormDefaultItemLocation = state =>
  selector(state, 'default_item_location');

const getStateShowMessageModal = state => state.item_location.showMessageModal;

const getItemCreatePermission = state => {
  const permissions = state.permissions.ui || {};

  return permissions['feature:item_management:create'];
};

const getStateIsProcessing = state => state.item_location.isProcessing;
const getStateSuccessProcess = state => state.item_location.successProcess;

const getStateNoOfItems = state => state.item_location.noOfItems;

export const getCustomFields = createSelector(
  [getFormCustomFields],
  customFields => customFields,
);

export const getCustomTabs = createSelector(
  [getFormCustomTabs],
  customTabs => customTabs,
);

export const getShowMessageModal = createSelector(
  [getStateShowMessageModal],
  showMessageModal => showMessageModal
);

export const getIsProcessing = createSelector(
  [getStateIsProcessing],
  isProcessing => isProcessing
);

export const isProcessSuccess = createSelector(
  [getStateSuccessProcess],
  successProcess => successProcess
);

export const getNoOfItems = createSelector(
  [getStateNoOfItems],
  noOfItems => noOfItems
);

export const getFormValueId = createSelector(
  [getFormItemLocationId],
  itemLocationId => {
    return itemLocationId;
  }
);

export const getSelectedItem = state => {
  const dataById = state.dataById || {};
  const data = dataById.data || {};
  return data;
};

export const getItemDetails = createSelector(
  [getSelectedItem, getFormCustomFields, getFormCustomTabs],
  (selectedItem, customFields, customTabs) => {
    const itemId = selectedItem.id;
    const itemType = selectedItem.item_type || {};
    let parentDetails = {};
    if(selectedItem.parent){
      const parent = (selectedItem.parent || {});
      const parentItemType = selectedItem.parent_item_type;
      parentDetails.matrix_parent_item_id = parent.id;
      parentDetails.parent_item_type = parentItemType;
    }

    return {
      custom_fields: customFields,
      custom_tabs: customTabs,
      item_id: itemId,
      item_type: itemType,
      ...parentDetails,
    };
  }
);

export const getShowApplyToAllButton = createSelector(
  [getFormDefaultItemLocation, getItemCreatePermission],
  (defaultItemLoc, allowCreate) => {
    return defaultItemLoc && allowCreate;
  }
);

export const showRetailPrice = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [
      DISCOUNT,
      COUPON,
      PROMOTION,
      PAYMENT_SURCHARGE,
      SERVICE_CHARGE,
      GRATUITY
    ];
    return itemTypes.indexOf(itemTypeId) === -1;
  }
);

export const showPricebook = createSelector([getFormItemTypeId], itemTypeId => {
  const itemTypes = [
    DISCOUNT,
    COUPON,
    PROMOTION,
    PAYMENT_SURCHARGE,
    SERVICE_CHARGE,
    GRATUITY
  ];
  return itemTypes.indexOf(itemTypeId) === -1;
});

export const showDiscountPercentage = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [DISCOUNT, COUPON];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showDiscountAmount = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [DISCOUNT, COUPON];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showChargePercentage = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [PAYMENT_SURCHARGE, SERVICE_CHARGE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showChargeAmount = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [PAYMENT_SURCHARGE, SERVICE_CHARGE, GRATUITY];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showSupplierRetailPrice = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [
      INVENTORY,
      NONINVENTORY,
      MATRIX,
      SERIALIZED,
      BATCH,
      BUNDLE
    ];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showFloorPrice = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showTaxGroup = createSelector([getFormItemTypeId], itemTypeId => {
  const itemTypes = [
    INVENTORY,
    NONINVENTORY,
    MATRIX,
    SHIPPING,
    SERIALIZED,
    BATCH,
    SERVICE,
    BUNDLE,
    GIFTCERTIFICATE,
    LOYALTY,
    PAYMENT_SURCHARGE,
    GRATUITY,
  ];
  return itemTypes.indexOf(itemTypeId) > -1;
});

export const showLoyaltyMultiplier = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [
      INVENTORY,
      NONINVENTORY,
      MATRIX,
      SERIALIZED,
      BATCH,
      BUNDLE
    ];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showPurchaseCost = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [
      INVENTORY,
      NONINVENTORY,
      MATRIX,
      SERIALIZED,
      BATCH,
      BUNDLE
    ];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showMarkdownPrice = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [
      INVENTORY,
      NONINVENTORY,
      MATRIX,
      SERIALIZED,
      BATCH,
      BUNDLE
    ];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showLandedCost = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showShippingCost = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showShippingHandling = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showMinReorderPoint = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showMaxReorderPoint = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showSafetyStock = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showBackOrderQty = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showBackOrderEta = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showLastReplenishment = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showAvailableForPickup = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showAvailableToShip = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showAvailableToTransfer = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showAllowManualDiscount = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [
      INVENTORY,
      NONINVENTORY,
      MATRIX,
      SHIPPING,
      SERIALIZED,
      BATCH,
      SERVICE,
      DISCOUNT,
      BUNDLE,
      GIFTCERTIFICATE,
      LOYALTY
    ];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);


export const showAllowAutoDiscount = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [
      SHIPPING,
      GIFTCERTIFICATE,
      LOYALTY,
      PAYMENT_SURCHARGE,
    ];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showAllowTaxExempt = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [
      BATCH,
      BUNDLE,
      GIFTCERTIFICATE,
      INVENTORY,
      MATRIX,
      NONINVENTORY,
      SERIALIZED,
    ];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showAvailableOnWeb = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [
      INVENTORY,
      NONINVENTORY,
      MATRIX,
      SHIPPING,
      SERIALIZED,
      BATCH,
      SERVICE,
      DISCOUNT,
      BUNDLE,
      GIFTCERTIFICATE,
      LOYALTY,
      PROMOTION,
      COUPON
    ];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const showReorder = createSelector([getFormItemTypeId], itemTypeId => {
  const itemTypes = [INVENTORY, MATRIX, SERIALIZED, BATCH, BUNDLE];
  return itemTypes.indexOf(itemTypeId) > -1;
});

export const showLocation = createSelector(
  [getFormItemTypeId, getFormItemLocationId],
  (itemTypeId, itemLocationId) => {
    const itemTypes = [INVENTORY, BUNDLE, SERIALIZED, BATCH];
    return Boolean(itemTypes.indexOf(itemTypeId) > -1 && itemLocationId);
  }
);

export const showReserve = createSelector(
  [getFormItemTypeId, getFormItemLocationId],
  (itemTypeId) => {
    const itemTypes = [INVENTORY, BUNDLE, SERIALIZED, BATCH];
    return Boolean(itemTypes.indexOf(itemTypeId) > -1);
  }
);

export const showSupplier = createSelector([getFormItemTypeId], itemTypeId => {
  const itemTypes = [INVENTORY, NONINVENTORY, MATRIX, SERIALIZED, BATCH];
  return itemTypes.indexOf(itemTypeId) > -1;
});

export const showOutputDevice = createSelector(
  [getFormItemTypeId],
  itemTypeId => {
    const itemTypes = [
      INVENTORY,
      NONINVENTORY,
      MATRIX,
      SHIPPING,
      SERIALIZED,
      BATCH,
      SERVICE,
      BUNDLE,
      LOYALTY
    ];
    return itemTypes.indexOf(itemTypeId) > -1;
  }
);

export const getItemLocationFilter = createSelector(
  [getFilter],
  (filter) => filter
);

export const getCurrencyCode = createSelector(
  [getFormLocationCurrency],
  currency => (currency || {}).code || 'USD'
);


export const getCurrencySymbol = createSelector(
  [getFormLocationCurrency],
  currency => (currency || {}).symbol
);


export const getCurrencyId = createSelector(
  [getFormLocationCurrency],
  currency => (currency || {}).id
);
